import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { YouthNixiVideo } from '../components/YouthNixiVideo'
import { useApp } from '../store/AppContext'
import { loadYouthSupportPlan } from '../lib/youthSupportPlan'
import { availableFoodBites, earnedChildPoints, feedCompanion, loadCompanionState, saveCompanionState, type CompanionColour } from '../lib/youthRewards'

const colourOptions: Array<{ value: CompanionColour; label: string; dot: string }> = [
  { value: 'pink', label: 'Pink', dot: 'bg-pink-400' },
  { value: 'blue', label: 'Blue', dot: 'bg-sky-400' },
  { value: 'green', label: 'Green', dot: 'bg-green-400' },
]

type CompanionMoment = 'idle' | 'wave' | 'excited' | 'flying'

const companionRotation: CompanionMoment[] = ['idle', 'wave', 'flying', 'excited']

const greenMomentDurations: Record<CompanionMoment, number> = {
  idle: 1500,
  wave: 1500,
  flying: 2000,
  excited: 2000,
}

export function Companion() {
  const { currentUser } = useApp()
  const participantId = currentUser.id === 'u-jnr' ? 'u-jnr' : 'u-leo'
  const [state, setState] = useState(() => loadCompanionState(participantId))
  const [bites, setBites] = useState(() => availableFoodBites(participantId))
  const [points, setPoints] = useState(() => earnedChildPoints(participantId))
  const [plan, setPlan] = useState(() => loadYouthSupportPlan(participantId))
  const [reaction, setReaction] = useState('Tap me or feed me!')
  const [eating, setEating] = useState(false)
  const [celebrating, setCelebrating] = useState(false)
  const [moment, setMoment] = useState<CompanionMoment>('idle')
  const [animationVersion, setAnimationVersion] = useState(0)
  const [feedBurst, setFeedBurst] = useState(0)
  const reactionTimer = useRef<number | null>(null)
  const eatingTimer = useRef<number | null>(null)

  const refresh = () => {
    setState(loadCompanionState(participantId))
    setBites(availableFoodBites(participantId))
    setPoints(earnedChildPoints(participantId))
  }

  useEffect(() => {
    const refreshPlan = () => setPlan(loadYouthSupportPlan(participantId))
    window.addEventListener('schub:youth-rewards', refresh)
    window.addEventListener('schub:youth-support-plan', refreshPlan)
    return () => {
      window.removeEventListener('schub:youth-rewards', refresh)
      window.removeEventListener('schub:youth-support-plan', refreshPlan)
    }
  }, [participantId])

  useEffect(() => {
    setState(loadCompanionState(participantId))
    setBites(availableFoodBites(participantId))
    setPoints(earnedChildPoints(participantId))
    setMoment('idle')
  }, [participantId])

  useEffect(() => {
    if (celebrating || eating) return
    const timer = window.setTimeout(() => {
      setMoment((current) => companionRotation[(companionRotation.indexOf(current) + 1) % companionRotation.length])
      setAnimationVersion((value) => value + 1)
    }, state.colour === 'pink' ? 6200 : greenMomentDurations[moment])
    return () => window.clearTimeout(timer)
  }, [moment, celebrating, eating, state.colour])

  useEffect(() => () => {
    if (reactionTimer.current !== null) window.clearTimeout(reactionTimer.current)
    if (eatingTimer.current !== null) window.clearTimeout(eatingTimer.current)
  }, [])

  const showReaction = (nextMoment: CompanionMoment, duration = 4300) => {
    setMoment(nextMoment)
    setCelebrating(true)
    setAnimationVersion((value) => value + 1)
    if (reactionTimer.current !== null) window.clearTimeout(reactionTimer.current)
    reactionTimer.current = window.setTimeout(() => setCelebrating(false), duration)
  }

  const feed = () => {
    if (feedCompanion(participantId, 5)) {
      setReaction('Yum! Crunch crunch! 😋')
      setEating(true)
      setCelebrating(true)
      setFeedBurst((value) => value + 1)
      setAnimationVersion((value) => value + 1)
      if (eatingTimer.current !== null) window.clearTimeout(eatingTimer.current)
      eatingTimer.current = window.setTimeout(() => {
        setEating(false)
        setCelebrating(false)
        setMoment('idle')
        setAnimationVersion((value) => value + 1)
      }, state.colour === 'pink' ? 4000 : 7000)
      refresh()
    } else setReaction('I need 5 Food Bites to eat 🍪')
  }

  const tickle = () => {
    setReaction('Hehe! That tickles! 😄')
    showReaction('excited', state.colour === 'pink' ? 4300 : 2000)
  }

  const chooseColour = (colour: CompanionColour) => {
    const updated = { ...state, colour }
    saveCompanionState(participantId, updated)
    setState(updated)
    setReaction(`${colour[0].toUpperCase()}${colour.slice(1)} looks great! ✨`)
    showReaction('excited', colour === 'pink' ? 4300 : 2000)
  }

  const progress = Math.min(100, Math.round((points / Math.max(1, plan.rewardGoal.targetPoints)) * 100))

  const videoVariant = eating ? 'eating' : moment === 'wave' ? 'wave' : moment === 'flying' ? 'flying' : moment === 'excited' ? 'celebrate' : 'subtle'

  return <div className="min-h-screen bg-gradient-to-b from-violet-200 via-pink-100 to-amber-100 pb-28">
    <div className="flex items-center gap-3 px-4 pt-5"><Link to="/today" className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-xl font-black shadow-card">←</Link><div><p className="text-sm font-extrabold text-violet-700">My Marnixia companion</p><h1 className="text-3xl font-black text-slate-900">Nixi™</h1></div></div>
    <div className="px-4 py-6">
      <div className="rounded-[2.5rem] bg-white/90 p-6 text-center shadow-card">
        <button onClick={tickle} className="relative mx-auto aspect-[55/67] w-full max-w-[28rem] overflow-hidden rounded-[2rem] bg-violet-100" aria-label="Play with Nixi"><div className="h-full w-full"><YouthNixiVideo className="h-full w-full object-cover" label="Nixi on the youth My NIXI screen" variant={videoVariant} colour={state.colour} restartToken={animationVersion} preloadAllColours /></div>{eating && <span key={feedBurst} className="youth-feeding-effects" aria-hidden="true">{Array.from({ length: 5 }, (_, index) => <i key={index} className={`youth-food-bite youth-food-bite--${index + 1}`} />)}</span>}</button>
        <p className="mt-4 text-xl font-black text-slate-800" aria-live="polite">{reaction}</p>
        <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full bg-amber-100 px-5 py-3 text-lg font-black text-amber-900">🍪 {bites} Nixi™ Food Bites</div>
        <p className="mx-auto mt-3 max-w-xs text-sm font-semibold text-slate-500">When you complete a step, you earn a point and Nixi™ earns a Food Bite. You are working as a team.</p>
        <div className="mt-6"><p className="mb-3 text-sm font-black text-slate-700">Choose Nixi™ colour</p><div className="grid grid-cols-3 gap-2">{colourOptions.map((option) => <button key={option.value} onClick={() => chooseColour(option.value)} className={`rounded-2xl border-2 px-2 py-3 text-sm font-black transition ${state.colour === option.value ? 'border-violet-600 bg-violet-50 text-violet-800' : 'border-slate-200 bg-white text-slate-600'}`}><span className={`mx-auto mb-2 block h-7 w-7 rounded-full ${option.dot}`} />{option.label}</button>)}</div></div>
        <button onClick={feed} disabled={eating} className="mt-6 w-full rounded-3xl bg-violet-600 py-4 text-lg font-black text-white shadow-card active:scale-[0.99] disabled:opacity-70">🍪 Feed Nixi™ · 5 bites</button>
      </div>
      <div className="mt-4 rounded-3xl bg-white/90 p-5 shadow-card"><p className="text-sm font-black text-violet-700">⭐ Team Rewards</p><h2 className="mt-1 text-xl font-black text-slate-900">{plan.rewardGoal.icon} {plan.rewardGoal.title}</h2><p className="mt-2 text-sm font-bold text-slate-600">My points: {points} / {plan.rewardGoal.targetPoints}</p><div className="mt-3 h-4 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-violet-600" style={{ width: `${progress}%` }} /></div><div className="mt-4 grid grid-cols-2 gap-3"><div className="rounded-2xl bg-violet-50 p-4 text-center"><p className="text-2xl">⭐</p><p className="mt-1 text-lg font-black text-violet-900">{points}</p><p className="text-xs font-bold text-violet-700">My points</p></div><div className="rounded-2xl bg-amber-50 p-4 text-center"><p className="text-2xl">🍪</p><p className="mt-1 text-lg font-black text-amber-900">{bites}</p><p className="text-xs font-bold text-amber-700">Nixi™ bites</p></div></div></div>
    </div>
  </div>
}

